interface ImageValidationResult {
  isValid: boolean
  error?: string
  processedImage?: string
}

export const MAX_IMAGE_SIZE_MB = 5 // Increased to 5MB
export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
] as const

export const MAX_DIMENSION = 1200 // Increased for better quality

export const validateAndProcessImage = async (file: File): Promise<ImageValidationResult> => {
  // Check file type
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type as typeof ACCEPTED_IMAGE_TYPES[number])) {
    return {
      isValid: false,
      error: `Invalid file type. Please upload one of the following: Images (JPG, PNG, WebP), Documents (PDF, DOC, DOCX), or Spreadsheets (XLS, XLSX)`
    }
  }

  // Check file size
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
    return {
      isValid: false,
      error: `File size exceeds ${MAX_IMAGE_SIZE_MB}MB limit`
    }
  }

  // Only process image files, return other files as valid without processing
  if (!file.type.startsWith('image/')) {
    return {
      isValid: true
    }
  }

  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height && width > MAX_DIMENSION) {
          height = Math.round((height * MAX_DIMENSION) / width)
          width = MAX_DIMENSION
        } else if (height > MAX_DIMENSION) {
          width = Math.round((width * MAX_DIMENSION) / height)
          height = MAX_DIMENSION
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve({
            isValid: false,
            error: 'Failed to get canvas context'
          })
          return
        }

        ctx.drawImage(img, 0, 0, width, height)

        // Convert to WebP for better compression
        const processedImage = canvas.toDataURL('image/webp', 0.8)
        resolve({
          isValid: true,
          processedImage
        })
      }

      img.onerror = () => {
        resolve({
          isValid: false,
          error: 'Failed to load image'
        })
      }

      img.src = e.target?.result as string
    }

    reader.onerror = () => {
      resolve({
        isValid: false,
        error: 'Failed to read file'
      })
    }

    reader.readAsDataURL(file)
  })
}
